import EnrichmentSettingsApiService from '../../../api/enrichmentSettingsApiService';
import { ISaveSettingsRequest, IVocabulariesSettingsResponse } from '../../../api/models/enrichmentSettings';
import { IMainStore, ISafeExecuteAsyncAction } from '../../../typings/stores';
import ModalWindow from '../../modal/modalWindow';
import EnrichmentSettings from './enrichmentSettings';
import { RupLoader } from 'copyright-clearance-center-common-ui-react';
import { inject, observer } from 'mobx-react';
import React, { useRef } from 'react';

export interface ISemanticEnrichmentSettingsDialogProps {
	open: boolean;
	onClose: () => void;
	onSave: (enrichmentEnabled: boolean, orderedIds: number[], orderedKeys: string[]) => void;
	switchable?: boolean;
	apiService: EnrichmentSettingsApiService;
	safeExecuteAsync?: ISafeExecuteAsyncAction;
}

const SemanticEnrichmentSettingsDialog: React.FunctionComponent<ISemanticEnrichmentSettingsDialogProps> = props => {
	const [vocabs, setVocabs] = React.useState<IVocabulariesSettingsResponse>(null);
	const [vocabDbOrder, setVocabDbOrder] = React.useState('');
	const [vocabUiOrder, setVocabUiOrder] = React.useState<number[]>([]);
	const [enrichmentEnabled, setEnrichmentEnabled] = React.useState(false);
	const [isLoading, setIsLoading] = React.useState(false);
	const orderedKeys = useRef([]);

	const onVocabOrderChanged = (orderForDb: string, orderForUi: number[], keys: string[]) => {
		setVocabDbOrder(orderForDb);
		setVocabUiOrder(orderForUi);
		orderedKeys.current = keys;
	};

	const getVocabs = async () => {
		const response = await props.safeExecuteAsync(() => props.apiService.fetchVocabularies(), null);
		setVocabs(response);
	};

	const saveSettingsAsync = async () => {
		const data = {
			vocabOrder: vocabDbOrder,
			disableSemanticEnrichment: !enrichmentEnabled
		} as ISaveSettingsRequest;
		const response = await props.safeExecuteAsync(() => props.apiService.saveSettings(data), false);
		return response;
	};

	React.useEffect(() => {
		getVocabs();
	}, []);

	const cancelButton = (
		<button key={'cancel'} className="rup-btn rup-btn--lg" type="button" onClick={() => props.onClose()}>
			{localize('Cancel')}
		</button>
	);

	const saveButton = (
		<button
			key={'save'}
			className="rup-btn rup-btn--lg rup-btn--contained"
			type="button"
			onClick={async () => {
				setIsLoading(true);
				const result = await props.safeExecuteAsync(() => saveSettingsAsync(), false);

				if (result) {
					props.onSave(enrichmentEnabled, vocabUiOrder, orderedKeys.current);
				}

				setIsLoading(false);
				props.onClose();
			}}
		>
			{localize('Save')}
		</button>
	);

	const dialogProps = {
		open: props.open,
		title: localize('Semantic enrichment settings'),
		content:
			vocabs === null ? (
				<RupLoader style={{ height: '31.5rem', maxHeight: '31.5rem' }} />
			) : (
				<EnrichmentSettings
					vocabularies={vocabs.semanticVocabularies}
					vocabColorPalette={vocabs.vocabColorPalette}
					semanticSearchEnabled={vocabs.semanticSearchEnabled}
					switchable={props.switchable}
					onEnrichmentEnabledChanged={setEnrichmentEnabled}
					onVocabOrderChanged={onVocabOrderChanged}
					isLoading={isLoading}
				/>
			),
		closeHandler: props.onClose,
		width: '46.5rem',
		actions: [cancelButton, saveButton]
	};

	return <ModalWindow {...dialogProps} />;
};

export default inject((stores: IMainStore) => ({
	safeExecuteAsync: stores.safeExecuteAsync
}))(observer(SemanticEnrichmentSettingsDialog));
